import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView } from 'react-native'
import { Button, Text, Spinner, Toggle, Radio } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveRoles, deleteRole } from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import { HintView } from '../../components/HintView'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { storesDictAtom, usersListAtom } from '../../global/AtomsProvider'
import { useForm, Controller } from 'react-hook-form'
import { ALL_PERMISSIONS } from '../../utils/permissionsConstants'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'

// Re-export for backward compatibility
export { ALL_PERMISSIONS }

export const SALARY_TYPES = [
  { id: 'none' },
  { id: 'hour' },
  { id: 'day' },
  { id: 'month' },
]

export const ROLE_BONUS_TYPES = [
  { id: 'individualSalesPercent' },
  { id: 'monthlyPlan' }, // old
]

export default function RoleDetailScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const { detail, detailIndex, allData } = route.params
  const { setValue, handleSubmit, control, watch, formState: { errors } } = useForm({
    defaultValues: {
      id: detail?.id ?? ('role-' + Date.now()),
      name: detail?.name ?? '',
      salaryType: detail?.salaryType ?? 'none',
      salary: detail?.salary?.toString?.() ?? '',
      roleBonusType: detail?.roleBonusType ?? null,
      individualSalesPercent: detail?.individualSalesPercent?.toString?.() ?? '0',
      minIndividualSales: detail?.minIndividualSales?.toString?.() ?? '0',
      permissions: detail ? (detail.permissions || {}) : {}
    }
  });
  const usersList = useAtomValue(usersListAtom)
  const watchSalaryType = watch('salaryType')
  const watchRoleBonusType = watch('roleBonusType')
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // Emit detail data for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[ROLE_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        name: detail.name,
        salaryType: detail.salaryType,
        salary: detail.salary,
        roleBonusType: detail.roleBonusType,
        individualSalesPercent: detail.individualSalesPercent,
        minIndividualSales: detail.minIndividualSales,
        permissions: detail.permissions,
      })));
    }
  }, [detail]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            if (detail.id === 'admin') {
              alert(t('roles.deleteAdminError'))
              return
            } 
            // check if there are no active users left in the role
            const activeUsers = usersList.filter(user => user.role == detail.id && user.active)
            if (activeUsers.length > 0) {
              alert(t('roles.deleteWithActiveUsersError') + activeUsers.map(user => user.name).join(', '))
              return
            }
            deleteRole(context.db, detailIndex, allData)
            navigation.goBack()
          }}
          iconName='trash-outline'
        />
      ),
      title: (detail?.name || '')
    })
  }, [])

  const onSubmit = async data => {
    setIsLoading(true)
    setSaveError('')
    let info = {...data, organizationId: context.user.organizationId }
    if (data.salaryType === 'none') {
      info.salary = null
      info.roleBonusType = null
      info.individualSalesPercent = null
      info.minIndividualSales = null
    } else {
      info.salary = isNaN(parseInt(data.salary)) ? 0 : parseInt(data.salary),
      info.individualSalesPercent = isNaN(parseInt(data.individualSalesPercent)) ? 0 : parseInt(data.individualSalesPercent)
      info.minIndividualSales = isNaN(parseInt(data.minIndividualSales)) ? 0 : parseInt(data.minIndividualSales)
    }
    try {
      await saveRoles(context.db, info, detailIndex, allData)
      setSuccessMessageVisible(true)
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
      setIsLoading(false)
    }
  }

  const handleFormErrors = (data) => {
    console.log(errors)
    setSaveError(t('notAllFieldsFilled'))
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id}
        </Text>}
    
        <Text category='label' style={styles.label}>
          {t('settings.name')}
        </Text>
        <Controller
          name="name"
          rules={{ required: true }}
          control={control}
          render={({field: { onChange, onBlur, value }}) => (
            <StyledTextInput 
              value={value}
              onChangeText={text => onChange(text)}
              onBlur={onBlur}
              returnKeyType='done'
              autoCapitalize='sentences'
            />
          )}
        />

        <HintView 
          text={t('roles.addingWorkingHoursHint')} 
          iconName='alert-circle-outline' style={{marginVertical: 10}} 
        />

        <Text category='label' style={styles.label}>{t('roles.salaryType')}</Text>
        <Controller
          name="salaryType"
          rules={{ required: true }}
          control={control}
          render={({field: { onChange, onBlur, value }}) => (
            <>
              { SALARY_TYPES.map((item, _index) => (
                <Radio testID={`radio-salary-${item.id}`} key={item.id} style={styles.radio} checked={value === item.id} onChange={(checked) => {if (checked) onChange(item.id)}}>
                  {t('roles.' + item.id)}
                </Radio>
              ))}
            </>
          )}
        />
        
        { watchSalaryType !== 'none' && (
          <>
            <Text category='label' style={styles.label}>
              {t('roles.' + watchSalaryType)}
            </Text>
            <Controller
              name="salary"
              rules={{ required: watchSalaryType !== 'none' ? true : false }}
              control={control}
              render={({field: { onChange, onBlur, value }}) => (
                <StyledTextInput 
                  value={value}
                  onChangeText={text => onChange(text)}
                  onBlur={onBlur}
                  returnKeyType='done'
                />
              )}
            />
          
            <Text category='label' style={styles.label}>{t('roles.roleBonusType')}</Text>
            <Controller
              name="roleBonusType"
              control={control}
              render={({field: { onChange, onBlur, value }}) => (
                <>
                  <Radio style={styles.radio} checked={!value} onChange={(checked) => {if (checked) onChange(null)}}>
                    {t('roles.withoutBonuses')}
                  </Radio>
                  <Radio style={styles.radio} checked={value === 'individualSalesPercent'} onChange={(checked) => {if (checked) onChange('individualSalesPercent')}}>
                    {t('roles.individualSalesPercent')}
                  </Radio>
                </>
              )}
            />
            { watchRoleBonusType === 'individualSalesPercent' && (
              <>
                <HintView 
                  text={t('roles.individualSalesPercentMinimumSalesHint')} 
                  iconName='alert-circle-outline' style={{marginVertical: 10}} 
                />
                <Text category='label' style={styles.label}>
                  {t('roles.individualSalesPercentForBonuses')}
                </Text>
                <Controller
                  name="individualSalesPercent"
                  rules={{ required: watchRoleBonusType === 'individualSalesPercent' ? true : false, min: 1, max: 100 }}
                  control={control}
                  render={({field: { onChange, onBlur, value }}) => (
                    <StyledTextInput 
                      value={value}
                      onChangeText={text => onChange(text)}
                      onBlur={onBlur}
                      returnKeyType='done'
                      keyboardType='number-pad'
                      placeholder='1-100'
                    />
                  )}
                />
                <Text category='label' style={styles.label}>
                  {t('roles.minIndividualSalesForDailySales')}
                </Text>
                <Controller
                  name="minIndividualSales"
                  rules={{ required: watchRoleBonusType === 'individualSalesPercent' ? true : false, min: 0 }}
                  control={control}
                  render={({field: { onChange, onBlur, value }}) => (
                    <StyledTextInput 
                      value={value}
                      onChangeText={text => onChange(text)}
                      onBlur={onBlur}
                      returnKeyType='done'
                      keyboardType='number-pad'
                      placeholder='50000'
                    />
                  )}
                />
              </>
            )}
          </>
        )}
        
        <Text category='label' style={styles.label}>
          {t('roles.permissions')}
        </Text>
        <View style={{flexDirection: 'row'}}>
          <Button appearance='ghost' onPress={() => {
            var allPermissions = {}
            ALL_PERMISSIONS.map(([key, value]) => (
              allPermissions[key] = true
            ))
            setValue('permissions', allPermissions)
          }}>{t('roles.chooseAll')}</Button>
          <Button appearance='ghost' onPress={() => {
            var allPermissions = {}
            ALL_PERMISSIONS.map(([key, value]) => (
              allPermissions[key] = false
            ))
            setValue('permissions', allPermissions)
          }}>{t('roles.clear')}</Button>
        </View>
        <View style={{flexWrap: 'wrap',  flexDirection: 'row'}}>
        { ALL_PERMISSIONS.map((key, _index) => (
          <Controller
            key={key}
            control={control}
            render={({field: { onChange, onBlur, value }}) => (
              <Toggle 
                checked={detail?.id === 'admin' ? true : value[key]}
                onChange={(newPermissionValue) => {
                  var newR = {...value}
                  newR[key] = newPermissionValue
                  onChange(newR)
                }}
                style={styles.toggleField}
                disabled={detail?.id === 'admin'}
              >
                {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('permissions.'+key)}</Text>}
              </Toggle>
            )}
            name="permissions"
            rules={{ required: true }}
          />
        ))}
        </View>
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={() => {
            handleSubmit(onSubmit, handleFormErrors)()
          }} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 300}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  radio: {
    marginBottom: 5,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  toggleField: {
    // flex: 1,
    marginBottom: 5,
    alignSelf: 'flex-start'
  },
  toggleText: {
   flexShrink: 1
  },
});